import { useEffect, useState } from 'react';
import { type TranslationKey, type Translator } from '@icebreaker/shared/i18n';
import { STAFF_LIMITS, type StaffAccountDto } from '@icebreaker/shared/staff';
import { ERROR_CODES } from '@icebreaker/shared/errors';
import {
  Avatar,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorBox,
  Skeleton,
  Table,
  Tag,
  tableStyles,
  useErrorMessage,
} from '@icebreaker/ui';
import { selectStaff } from '@/features/auth/store/auth.selectors';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  cancelStaffInvitation,
  fetchStaffAccounts,
  mutationReset,
  noticeDismissed,
  reinviteStaff,
  suspendStaff,
  unsuspendStaff,
} from '../store/staff-accounts.slice';
import { InviteStaffDialog } from './invite-staff-dialog';
import styles from './admin.module.css';

type RowActionKind = 'reinvite' | 'cancel' | 'suspend' | 'unsuspend';

interface PendingAction {
  readonly kind: RowActionKind;
  readonly account: StaffAccountDto;
}

/**
 * PERSONEL HESAPLARI (panel kimliği ayrımı · Karar 4c) — `/panel/staff*`
 * altı ucun arayüzü: liste · davet · yeniden davet · davet iptali · askı ·
 * askı kaldırma. Yalnız `staff:account_manage` (admin) görür.
 *
 * REFERANS YOKTUR (devir paketi 25–27 ürün yönetimidir); anatomi kullanıcı
 * ekranıyla (25) BİREBİR aynıdır: başlık satırı + meta · araç çubuğu · tablo
 * (avatar monogramlı kişi hücresi, README 4.6) · satır eylemleri (hayalet +
 * çerçeveli yıkıcı) · gerekçeli onay penceresi · denetim notu.
 *
 * PANELDE "ŞİFREMİ UNUTTUM" YOKTUR: şifresini kaybeden personelin tek yolu bu
 * ekrandaki YENİDEN DAVET'tir. Bu yüzden eylem şifresi kurulu hesaplarda da
 * durur, yalnız şifresizlerde değil.
 *
 * KENDİ SATIRI: yönetici kendini askıya alamaz (sunucu 400 döner) ve kendi
 * davetini yönetmesi anlamsızdır; satırda "Sen" rozeti ve eylem yerine not
 * çizilir — hiçbir zaman başarılamayacak bir düğme sunulmaz.
 */
export function StaffScreen() {
  const t = useTranslation();
  const errorMessage = useErrorMessage();
  const dispatch = useAppDispatch();
  const self = useAppSelector(selectStaff);
  const list = useAppSelector((state: RootState) => state.staffAccounts.list);
  const mutation = useAppSelector((state: RootState) => state.staffAccounts.mutation);
  const notice = useAppSelector((state: RootState) => state.staffAccounts.lastNotice);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);

  useEffect(() => {
    dispatch(fetchStaffAccounts.request());
  }, [dispatch]);

  // Satır eylemi başarılı → onay penceresi kapanır (liste saga'da tazelendi).
  useEffect(() => {
    if (mutation.phase === 'succeeded' && pending) {
      setPending(null);
      dispatch(mutationReset());
    }
  }, [mutation.phase, pending, dispatch]);

  const adminCount = list.items.filter((account) => account.role === 'admin').length;
  const loadingFirst = list.phase === 'loading' && list.items.length === 0;
  const loadedEmpty = list.phase === 'succeeded' && list.items.length === 0;
  const hours = STAFF_LIMITS.invitationTtlHours;

  const openAction = (kind: RowActionKind, account: StaffAccountDto) => {
    dispatch(mutationReset());
    setPending({ kind, account });
  };

  return (
    <section className={styles.screen}>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{t.t('admin.staff.title')}</h1>
        {list.phase === 'succeeded' ? (
          <span className={styles.titleMeta}>
            {t.t('admin.staff.headline', {
              total: t.formatNumber(list.items.length),
              admins: t.formatNumber(adminCount),
            })}
          </span>
        ) : null}
      </div>
      <p className={styles.lead}>{t.t('admin.staff.lead')}</p>

      <div className={styles.toolbar}>
        {/* Liste boşken tek dolu eylem boş durumun düğmesidir (README 4.1). */}
        <Button variant={loadedEmpty ? 'secondary' : 'primary'} onClick={() => setInviteOpen(true)}>
          {t.t('admin.staff.invite')}
        </Button>
      </div>

      {notice ? (
        <p className={styles.noticeBox} role="status">
          <span>
            {notice.kind === 'invited'
              ? t.t('admin.staff.invitationSent', { email: notice.email, hours })
              : t.t('admin.staff.invitationCancelled', { name: notice.email })}
          </span>
          <Button variant="ghost" size="sm" onClick={() => dispatch(noticeDismissed())}>
            {t.t('common.close')}
          </Button>
        </p>
      ) : null}

      <ErrorBox error={list.error} className={styles.errorBox}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => dispatch(fetchStaffAccounts.request())}
        >
          {t.t('common.retry')}
        </Button>
      </ErrorBox>

      <div className={styles.tableWrap}>
        {loadingFirst ? (
          <Skeleton label={t.t('admin.staff.loading')} variant="people" lines={4} />
        ) : loadedEmpty ? (
          <EmptyState
            title={t.t('admin.staff.emptyTitle')}
            body={t.t('admin.staff.emptyBody')}
            action={
              <Button variant="primary" onClick={() => setInviteOpen(true)}>
                {t.t('admin.staff.invite')}
              </Button>
            }
          />
        ) : list.items.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th>{t.t('admin.staff.table.person')}</th>
                <th>{t.t('admin.staff.table.role')}</th>
                <th>{t.t('admin.staff.table.status')}</th>
                <th>{t.t('admin.staff.table.lastLogin')}</th>
                <th>{t.t('admin.staff.table.createdAt')}</th>
                <th aria-hidden="true" className={styles.actionsCell} />
              </tr>
            </thead>
            <tbody>
              {list.items.map((account) => {
                const isSelf = account.id === self?.account.id;
                return (
                  <tr key={account.id}>
                    <td>
                      <span className={styles.personCell}>
                        <Avatar name={account.displayName} size="xs" />
                        <span style={{ minWidth: 0 }}>
                          <span className={styles.personName}>{account.displayName}</span>
                          <span className={styles.personMail}>{account.email}</span>
                        </span>
                      </span>
                    </td>
                    <td className={tableStyles.cellMuted}>
                      {t.t(`admin.staff.roles.${account.role}` as TranslationKey)}
                    </td>
                    <td>
                      <span className={styles.tagRow}>
                        <Tag variant={statusVariant(account)}>{statusLabel(t, account)}</Tag>
                        {account.passwordSet ? null : (
                          <Tag variant="warning">{t.t('admin.staff.passwordNotSet')}</Tag>
                        )}
                        {isSelf ? <Tag variant="neutral">{t.t('admin.staff.you')}</Tag> : null}
                      </span>
                    </td>
                    <td className={styles.cellMeta}>
                      {account.lastLoginAt
                        ? t.formatDate(account.lastLoginAt, { dateStyle: 'medium' })
                        : t.t('admin.staff.neverLoggedIn')}
                    </td>
                    <td className={styles.cellMeta}>
                      {t.formatDate(account.createdAt, { dateStyle: 'medium' })}
                    </td>
                    <td className={styles.actionsCell}>
                      {isSelf ? (
                        <span className={styles.cellMeta}>{t.t('admin.staff.selfNote')}</span>
                      ) : (
                        <span className={styles.rowActions}>
                          {/*
                            Yıkıcı satır eylemi kitin `dangerOutline` varyantıdır
                            (tech-lead: aynı turda oturum katmanı ZATEN onu
                            kullanıyordu; ekranda elle yazılmış ikinci bir düğme
                            aynı problemin iki deseni olurdu — CLAUDE.md "ortak
                            bileşen yeniden yazılmaz").
                          */}
                          {rowActions(account).map((kind) => (
                            <Button
                              key={kind}
                              variant={ACTION_VARIANT[kind]}
                              size="sm"
                              onClick={() => openAction(kind, account)}
                            >
                              {t.t(ACTION_LABEL[kind])}
                            </Button>
                          ))}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : null}
      </div>

      <p className={styles.decidedNote}>{t.t('admin.staff.auditNote')}</p>

      <InviteStaffDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />

      <ConfirmDialog
        open={pending !== null}
        tone={pending?.kind === 'suspend' || pending?.kind === 'cancel' ? 'negative' : 'positive'}
        title={
          pending ? t.t(ACTION_TITLE[pending.kind], { name: pending.account.displayName }) : ''
        }
        body={pending ? t.t(ACTION_BODY[pending.kind], { hours }) : undefined}
        confirmLabel={pending ? t.t(ACTION_LABEL[pending.kind]) : ''}
        cancelLabel={t.t('common.cancel')}
        loading={mutation.phase === 'loading'}
        error={mutationErrorText(t, errorMessage(mutation.error), mutation.error?.code, pending)}
        onConfirm={() => {
          if (!pending) return;
          const staffAccountId = pending.account.id;
          dispatch(
            pending.kind === 'reinvite'
              ? reinviteStaff.request({ staffAccountId })
              : pending.kind === 'cancel'
                ? cancelStaffInvitation.request({ staffAccountId, email: pending.account.email })
                : pending.kind === 'suspend'
                  ? suspendStaff.request({ staffAccountId })
                  : unsuspendStaff.request({ staffAccountId }),
          );
        }}
        onCancel={() => {
          setPending(null);
          dispatch(mutationReset());
        }}
      />
    </section>
  );
}

/**
 * SATIR EYLEMLERİ — hesabın durumundan türetilir, uydurulmaz:
 *  · askıda      → yalnız "askıyı kaldır" (aynı şifresiyle döner, davet gerekmez);
 *  · kapalı      → hiçbiri (kalıcı; yeni hesap açılır);
 *  · şifresiz    → yeniden davet + daveti iptal et (bekleyen bağlantı olabilir);
 *  · şifreli     → yeniden davet (kurtarma yolu) + askıya al.
 */
function rowActions(account: StaffAccountDto): readonly RowActionKind[] {
  if (account.status === 'suspended') return ['unsuspend'];
  if (account.status === 'disabled') return [];
  return account.passwordSet ? ['reinvite', 'suspend'] : ['reinvite', 'cancel'];
}

/**
 * Hayalet = kurtarma · çerçeveli yıkıcı = askı ve davet iptali · ikincil =
 * askıyı kaldırma. Davet iptali düğme standardıyla (kullanıcı kararı 19.10.2026,
 * "Daveti iptal et" olumsuz eylem) çerçeveli kırmızıya geçti; dolu kırmızı yalnız
 * onay penceresindedir. Varyant tablodan geldiği için standardın kapısı bu
 * satırı hâle bağlı sayar; eşleme burada elle tutulur.
 */
const ACTION_VARIANT: Readonly<Record<RowActionKind, 'ghost' | 'secondary' | 'dangerOutline'>> = {
  reinvite: 'ghost',
  cancel: 'dangerOutline',
  suspend: 'dangerOutline',
  unsuspend: 'secondary',
};

const ACTION_LABEL: Readonly<Record<RowActionKind, TranslationKey>> = {
  reinvite: 'admin.staff.reinvite',
  cancel: 'admin.staff.cancelInvitation',
  suspend: 'admin.staff.suspend',
  unsuspend: 'admin.staff.unsuspend',
};

const ACTION_TITLE: Readonly<Record<RowActionKind, TranslationKey>> = {
  reinvite: 'admin.staff.reinviteTitle',
  cancel: 'admin.staff.cancelTitle',
  suspend: 'admin.staff.suspendTitle',
  unsuspend: 'admin.staff.unsuspendTitle',
};

const ACTION_BODY: Readonly<Record<RowActionKind, TranslationKey>> = {
  reinvite: 'admin.staff.reinviteBody',
  cancel: 'admin.staff.cancelBody',
  suspend: 'admin.staff.suspendBody',
  unsuspend: 'admin.staff.unsuspendBody',
};

/**
 * Onay penceresindeki hata cümlesi. Alan-dışı adrese yeniden davet (politika
 * reddi) kurtarma yolunun KAPALI olduğu anlamına gelir; cümlenin yanına ne
 * yapılacağı da yazılır (kapanış kriteri 7 uyarısı).
 */
function mutationErrorText(
  t: Translator,
  sentence: string | null,
  code: string | undefined,
  pending: PendingAction | null,
): string | undefined {
  if (sentence === null) return undefined;
  if (pending?.kind === 'reinvite' && code === ERROR_CODES.STAFF_EMAIL_DOMAIN_NOT_ALLOWED) {
    return `${sentence} ${t.t('admin.staff.reinviteDomainNote')}`;
  }
  return sentence;
}

function statusLabel(t: Translator, account: StaffAccountDto): string {
  return t.t(`admin.staff.statuses.${account.status}` as TranslationKey);
}

function statusVariant(account: StaffAccountDto): 'outline' | 'danger' | 'neutral' {
  if (account.status === 'suspended') return 'danger';
  if (account.status === 'disabled') return 'neutral';
  return 'outline';
}
